import { formatCurrency } from '@/utils/numberFormat';
import { formatPercent } from '@/utils/percentFormat';
import { buildInitials } from '@/utils/textValue';
import { normalizedTabs } from '@/utils/tabNormalization';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  buildExpenseFeeStatusTabs,
  filterExpenseFeeByStatus,
} from '@/composables/expensesFees/shared/useExpenseFeeStatusTabs';
import {
  buildPaginationPayload,
  resolveTotalPages,
  sliceRowsByPage,
} from '@/composables/shared/usePagination';

const { catalog: text } = useDomainText('sales');

export const saleDetailComputed = {
  saleId() {
    return Number(this.$route?.params?.saleId || 0);
  },
  saleName() {
    return String(this.sale?.name || '').trim();
  },
  saleCode() {
    return String(this.sale?.code || '').trim();
  },
  saleStatusLabel() {
    return String(this.sale?.status_name || '').trim() || text.detail.labels.noStatus;
  },
  saleIsActive() {
    return this.saleStatusLabel.toLowerCase() !== text.detail.labels.inactiveStatus;
  },
  isMultiCustomerSale() {
    const countCandidates = [
      this.sale?.customers_count,
      this.sale?.companies_count,
      this.sale?.clients_count,
    ];

    const hasManyCustomers = countCandidates.some((candidate) => Number(candidate || 0) > 1);
    const hasCustomersArray = Array.isArray(this.sale?.customers) && this.sale.customers.length > 1;
    const multiFlag = Number(this.sale?.is_multi_customer || this.sale?.multi_customer || 0) === 1;
    const typeLabel = String(this.sale?.sale_type || this.sale?.type || this.sale?.sale_mode || '').trim().toLowerCase();

    return hasManyCustomers || hasCustomersArray || multiFlag || typeLabel.includes('multi');
  },
  isFlatRateSale() {
    const modeLabel = String(this.sale?.sale_mode || this.sale?.sale_type || this.sale?.type || '').trim().toLowerCase();
    if (modeLabel.includes('iguala') || modeLabel.includes('retainer')) {
      return true;
    }

    const serviceCode = String(this.sale?.service_type_code || '').trim().toUpperCase();
    if (serviceCode === 'IGL') {
      return true;
    }

    const serviceTypeLabel = String(this.sale?.service_type_name || this.sale?.service_type || '').trim().toLowerCase();
    return serviceTypeLabel.includes('iguala') || serviceTypeLabel.includes('retainer');
  },
  tabs() {
    const backendTabs = normalizedTabs(this.sale?.tabs);
    const hasDocumentsTab = backendTabs.some((tab) => String(tab?.key || '').trim() === 'documents');

    if (hasDocumentsTab) {
      return backendTabs;
    }

    const historyIndex = backendTabs.findIndex((tab) => String(tab?.key || '').trim() === 'history');
    const documentsTab = { key: 'documents', label: text.detail.labels.documentsTab };

    if (historyIndex >= 0) {
      return [
        ...backendTabs.slice(0, historyIndex),
        documentsTab,
        ...backendTabs.slice(historyIndex),
      ];
    }

    return [...backendTabs, documentsTab];
  },
  saleInitials() {
    return buildInitials(this.saleName || this.saleCode, { fallback: 'NG' });
  },
  headerActions() {
    return [
      {
        key: 'edit-sale',
        label: text.detail.headerActions.editSale,
        icon: 'fa-pencil',
        variant: 'secondary',
      },
      {
        key: 'sale-sheet',
        label: text.detail.headerActions.saleSheet,
        icon: 'fa-id-card-o',
        variant: 'primary',
      },
      {
        key: 'new-payment-plan',
        label: text.detail.headerActions.newPaymentPlan,
        icon: 'fa-list-alt',
        variant: 'secondary',
      },
      {
        key: 'new-expense',
        label: text.detail.headerActions.newExpense,
        icon: 'fa-credit-card',
        variant: 'secondary',
      },
      {
        key: 'new-fee',
        label: text.detail.headerActions.newFee,
        icon: 'fa-percent',
        variant: 'secondary',
      },
      {
        key: 'add-calendar',
        label: text.detail.headerActions.addCalendar,
        icon: 'fa-calendar-plus-o',
        variant: 'secondary',
      },
    ];
  },
  detailMetrics() {
    const metrics = this.saleMetrics || {};
    const saleSubtotal = Number(this.sale?.subtotal || this.sale?.sale_price || 0);
    const summary = metrics?.summary && typeof metrics.summary === 'object' ? metrics.summary : {};
    const toNumber = (value, fallback = 0) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const normalizeSummarySection = (section, fallbackTotal = 0) => {
      const source = section && typeof section === 'object' ? section : {};

      const total = toNumber(source.total, toNumber(fallbackTotal, 0));

      return {
        subtotal: toNumber(source.subtotal, total),
        transferred_taxes: toNumber(source.transferred_taxes, toNumber(source.transfered_taxes, 0)),
        withheld_taxes: toNumber(source.withheld_taxes, 0),
        total,
      };
    };

    const incomeSummary = normalizeSummarySection(summary.income || metrics.income_summary, metrics.income);
    const expenseSummary = normalizeSummarySection(summary.expenses || metrics.expenses_summary, metrics.expenses);
    const commissionsSummary = normalizeSummarySection(summary.commissions || metrics.commissions_summary, metrics.commissions);
    const utilityTotal = toNumber(metrics.utility, incomeSummary.total - expenseSummary.total - commissionsSummary.total);
    const utilitySubtotalComputed = incomeSummary.subtotal - expenseSummary.subtotal - commissionsSummary.subtotal;
    const utilitySubtotal = toNumber(
      metrics.utility_subtotal ?? metrics.utilitySubtotal,
      utilitySubtotalComputed
    );
    const marginTotal = toNumber(
      metrics.margin,
      incomeSummary.total > 0 ? ((utilityTotal / incomeSummary.total) * 100) : 0
    );
    const marginSubtotal = toNumber(
      metrics.margin_subtotal ?? metrics.marginSubtotal,
      incomeSummary.subtotal > 0 ? ((utilitySubtotal / incomeSummary.subtotal) * 100) : 0
    );

    const buildBreakdownItems = (section) => {
      const subtotalLabel = text.detail.metrics.subtotal || 'Subtotal';
      const transferredTaxesLabel = text.detail.metrics.transferredTaxes || 'Impuestos';
      const withheldTaxesLabel = text.detail.metrics.withheldTaxes || 'Retenciones';

      return [
        {
          label: `${subtotalLabel}:`,
          value: formatCurrency(section.subtotal),
          tone: 'base',
        },
        {
          label: `+ ${transferredTaxesLabel}:`,
          value: formatCurrency(section.transferred_taxes),
          tone: 'plus',
        },
        {
          label: `- ${withheldTaxesLabel}:`,
          value: formatCurrency(section.withheld_taxes),
          tone: 'minus',
        },
      ];
    };

    const utilitySubtotalLabel = text.detail.metrics.utilitySubtotal || `${text.detail.metrics.utility} ${text.detail.metrics.subtotal}`;
    const marginLabel = 'Margen';
    const marginSubtotalLabel = text.detail.metrics.marginSubtotal || `${text.detail.metrics.margin} ${text.detail.metrics.subtotal}`;

    const utilityBreakdownItems = [
      {
        label: `${marginLabel}:`,
        value: formatPercent(marginTotal),
        tone: 'base',
      },
      {
        label: `${utilitySubtotalLabel}:`,
        value: formatCurrency(utilitySubtotal),
        tone: 'base',
      },
      {
        label: `${marginSubtotalLabel}:`,
        value: formatPercent(marginSubtotal),
        tone: 'base',
      },
    ];

    return [
      {
        key: 'business-budget',
        title: text.detail.metrics.business,
        value: formatCurrency(saleSubtotal),
        valueMeta: '',
        extraValues: [],
        valueScale: 0.92,
        icon: 'fa-briefcase',
        iconBg: 'rgba(2, 132, 199, 0.12)',
        iconColor: '#0284c7',
      },
      {
        key: 'income',
        title: text.detail.metrics.income,
        value: formatCurrency(incomeSummary.total),
        valueMetaItems: buildBreakdownItems(incomeSummary),
        valueScale: 0.88,
        icon: 'fa-line-chart',
        iconBg: 'rgba(0, 112, 205, 0.12)',
        iconColor: '#0070cd',
      },
      {
        key: 'expenses',
        title: text.detail.metrics.expenses,
        value: formatCurrency(expenseSummary.total),
        valueMetaItems: buildBreakdownItems(expenseSummary),
        valueScale: 0.88,
        icon: 'fa-credit-card',
        iconBg: 'rgba(239, 68, 68, 0.12)',
        iconColor: '#ef4444',
      },
      {
        key: 'commissions',
        title: text.detail.metrics.commissions,
        value: formatCurrency(commissionsSummary.total),
        valueMetaItems: buildBreakdownItems(commissionsSummary),
        valueScale: 0.88,
        icon: 'fa-percent',
        iconBg: 'rgba(251, 146, 60, 0.16)',
        iconColor: '#ea580c',
      },
      {
        key: 'utility-margin',
        title: text.detail.metrics.utility,
        value: formatCurrency(utilityTotal),
        valueMetaItems: utilityBreakdownItems,
        valueScale: 0.88,
        icon: 'fa-area-chart',
        iconBg: 'rgba(59, 212, 46, 0.12)',
        iconColor: '#3bd42e',
      },
    ];
  },
  normalizedExpenseRows() {
    return Array.isArray(this.expenseRows) ? this.expenseRows : [];
  },
  expenseStatusTabs() {
    return buildExpenseFeeStatusTabs(this.normalizedExpenseRows);
  },
  filteredExpenseRows() {
    return filterExpenseFeeByStatus(this.normalizedExpenseRows, this.expenseActiveStatusTab);
  },
  expenseTotalPages() {
    return resolveTotalPages(this.filteredExpenseRows.length, this.expensePageSize);
  },
  paginatedExpenseRows() {
    return sliceRowsByPage(this.filteredExpenseRows, this.expenseCurrentPage, this.expensePageSize);
  },
  expensePagination() {
    return buildPaginationPayload({
      currentPage: this.expenseCurrentPage,
      pageSize: this.expensePageSize,
      totalItems: this.filteredExpenseRows.length,
    });
  },
  normalizedFeeRows() {
    return Array.isArray(this.feeRows) ? this.feeRows : [];
  },
  feeStatusTabs() {
    return buildExpenseFeeStatusTabs(this.normalizedFeeRows);
  },
  filteredFeeRows() {
    return filterExpenseFeeByStatus(this.normalizedFeeRows, this.feeActiveStatusTab);
  },
  feeTotalPages() {
    return resolveTotalPages(this.filteredFeeRows.length, this.feePageSize);
  },
  paginatedFeeRows() {
    return sliceRowsByPage(this.filteredFeeRows, this.feeCurrentPage, this.feePageSize);
  },
  feePagination() {
    return buildPaginationPayload({
      currentPage: this.feeCurrentPage,
      pageSize: this.feePageSize,
      totalItems: this.filteredFeeRows.length,
    });
  },
  hasExpenseApprovalModule() {
    const can = typeof this.$can === 'function' ? this.$can : null;
    if (!can) {
      return false;
    }

    return Boolean(
      can('expense_approval')
      || can('expenses_approbal')
      || can('expenses.approval')
    );
  },
  clearSaleExpensesConfirmSubtitle() {
    return this.hasExpenseApprovalModule
      ? text.detail.clearExpenses.subtitleApproved
      : text.detail.clearExpenses.subtitlePaid;
  },
  clearSaleExpensesConfirmMessage() {
    return this.hasExpenseApprovalModule
      ? text.detail.clearExpenses.messageApproved
      : text.detail.clearExpenses.messagePaid;
  },
  clearSaleFeesConfirmSubtitle() {
    return this.hasExpenseApprovalModule
      ? text.detail.clearFees.subtitleApproved
      : text.detail.clearFees.subtitlePaid;
  },
  clearSaleFeesConfirmMessage() {
    return this.hasExpenseApprovalModule
      ? text.detail.clearFees.messageApproved
      : text.detail.clearFees.messagePaid;
  },
};
